import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { InfoWindow, Polyline, useGoogleMap } from "@react-google-maps/api";
import {
    Box, CircularProgress, IconButton, LinearProgress, Link, List, ListItemButton, Stack, Tooltip, Typography
} from "@mui/material";
import DirectionsIcon from "@mui/icons-material/Directions";
import LocationSearchingIcon from "@mui/icons-material/LocationSearching";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import MapContainer from "../MapContainer";
import AdvancedMarker from "../AdvancedMarker";

// Colori della UGO Web Board: blu per selezione e percorso, arancio per i punti, rosso per il furto.
const BLUE = "#1a5fb4";
const BLUE_TINT = "#eef4fc";
const ORANGE = "#e2622b";
const RED = "#dd3333";
const RED_TINT = "#fdecec";
const GREEN = "#4caf50";
const AMBER = "#f59e0b";
const BOX_BG = "#f4f5f7";
const LINE = "#ececef";
const RADIUS = "14px";
const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";
const REFRESH_MS = 60_000;
const DEVICE_ZOOM = 16;
const BOARD_HEIGHT = { xs: "auto", md: 600 };
const MAP_HEIGHT = { xs: 320, md: "100%" };

// mode del server UGO: 0 fermo, 1 in sessione, 2 allarme furto
const MODES = {
    "0": { label: "pedalStateIdle", color: "#6b7280", bg: "#eef0f3" },
    "1": { label: "pedalStateSession", color: "#2e7d32", bg: "#e8f5e9" },
    "2": { label: "pedalStateAlarm", color: RED, bg: RED_TINT },
};

/** Segnaposto tondo: il primo e l'ultimo punto di un evento, e quello selezionato, sono piu' grandi. */
function dot(size, fill) {
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}">`
        + `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 1.5}" fill="${fill}" stroke="#fff" stroke-width="2"/></svg>`;
    return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize: { width: size, height: size } };
}

const DOT = dot(12, ORANGE);
const DOT_END = dot(20, ORANGE);
const DOT_FOCUS = dot(22, "#1a1a1a");

// orari: stringhe ISO 8601 UTC dal backend
const formatDateTime = iso => dayjs(new Date(iso)).format("DD/MM/YYYY HH:mm");
const formatDetection = iso => dayjs(new Date(iso)).format("DD/MM/YYYY — HH:mm:ss");
const formatTime = iso => dayjs(new Date(iso)).format("HH:mm:ss");
/** Coordinate come nella Web Board: 41.1177° N, 16.8719° E. */
const formatCoords = (lat, lng, digits) =>
    `${Math.abs(lat).toFixed(digits)}° ${lat >= 0 ? "N" : "S"}, ${Math.abs(lng).toFixed(digits)}° ${lng >= 0 ? "E" : "W"}`;
/** Nome breve del pedale, come lo mostrano pannello e scheda dei token. */
export const shortImei = imei => imei && imei.length > 4 ? imei.slice(-4) : imei;
const formatMinutes = m => m == null ? "-" : m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${m % 60}m`;

/** Istante di una stringa ISO per gli ordinamenti; senza data va in fondo. */
const time = iso => iso ? new Date(iso).getTime() : -Infinity;
/** Ultima posizione di un evento: quello in corso sta rilevando adesso, quello concluso alla sua fine. */
const lastAlertPosition = a => a.completedAt ? time(a.completedAt) : a.startedAt ? Infinity : -Infinity;
/** Confronto decrescente che regge gli infiniti (una sottrazione darebbe NaN fra due valori uguali). */
const desc = (x, y) => (y > x) - (y < x);
/** Eventi per ultima posizione rilevata; fra due in corso, prima quello iniziato dopo. */
const byLastAlertPosition = (x, y) =>
    desc(lastAlertPosition(x), lastAlertPosition(y)) || desc(time(x.startedAt), time(y.startedAt));

// pedali nascosti da chi guarda: restano solo nel suo browser, per IMEI
const HIDDEN_KEY = "pedalBoard.hidden";

/**
 * IMEI dei pedali che chi guarda ha nascosto dalla mappa. La scelta vive nel suo browser: chi apre il link
 * condiviso non cambia la vista del proprietario. Senza localStorage (finestra privata, dati bloccati) si
 * vede tutto, e la scelta vale finche' la pagina resta aperta.
 */
function useHiddenPedals() {
    const [hidden, setHidden] = useState(() => {
        try {
            return new Set(JSON.parse(window.localStorage.getItem(HIDDEN_KEY)) || []);
        } catch (e) {
            return new Set();
        }
    });
    const toggle = useCallback(imei => setHidden(prev => {
        const next = new Set(prev);
        if (next.has(imei)) next.delete(imei); else next.add(imei);
        try {
            window.localStorage.setItem(HIDDEN_KEY, JSON.stringify([...next]));
        } catch (e) {
            // nessun salvataggio: la scelta resta in memoria
        }
        return next;
    }), []);
    return [hidden, toggle];
}

/**
 * Pannello del pedale UGO, impaginato come la "UGO Web Board": a sinistra gli ultimi dati noti di ogni pedale
 * (stato, ultima rilevazione, coordinate, batteria) e l'elenco degli eventi registrati (sotto quello
 * selezionato, le sue posizioni con l'ora); a destra la mappa con l'ultima posizione dei pedali, sempre, e il
 * percorso dell'evento selezionato. Tutto dal server UGO: le sessioni non si mostrano.
 * Lo usano tre pagine (pinbiker, link condivisibile, tab admin): ognuna passa le funzioni che chiamano
 * il proprio endpoint. Si aggiorna ogni 60 secondi. Senza pedali sul server UGO mostra `empty`, se dato.
 * Ogni pedale si puo' nascondere dalla mappa: la sua scheda si riduce al nome e i suoi eventi spariscono.
 */
export default function PedalBoard({ queryKey, loadBoard, loadAlert, empty, height = BOARD_HEIGHT }) {
    const { t } = useTranslation();
    const [selected, setSelected] = useState(null);
    const [focused, setFocused] = useState(null);
    const [hidden, toggleHidden] = useHiddenPedals();

    const { data: board, status } = useQuery([...queryKey, "board"], loadBoard,
        { refetchInterval: REFRESH_MS, refetchOnWindowFocus: false, retry: false });
    // gli eventi di un pedale nascosto spariscono con lui: altrimenti il suo percorso tornerebbe sulla mappa;
    // in ordine di ultima posizione rilevata, quindi prima quelli in corso, poi per fine
    const alerts = useMemo(() => (board?.alerts || [])
        .filter(a => !hidden.has(a.imei))
        .sort(byLastAlertPosition), [board, hidden]);
    const summary = selected && alerts.find(a => a.imei === selected.imei && a.id === selected.id);

    // di default il piu' recente; se l'evento selezionato sparisce dall'elenco si torna al piu' recente
    useEffect(() => {
        if (alerts.length && !summary) setSelected({ imei: alerts[0].imei, id: alerts[0].id });
        if (!alerts.length && selected) setSelected(null);
    }, [alerts, summary, selected]);
    useEffect(() => setFocused(null), [selected?.imei, selected?.id]);

    // nella chiave anche fine e pacchetti: quando l'evento si chiude il dettaglio si rilegge una volta, con le ultime posizioni
    const { data: lastAlert, status: alertStatus } = useQuery(
        [...queryKey, "alert", selected?.imei, selected?.id, summary?.completedAt, summary?.points],
        () => loadAlert(selected.imei, selected.id),
        {
            enabled: !!summary, retry: false, refetchOnWindowFocus: false, keepPreviousData: true,
            refetchInterval: summary && !summary.completedAt ? REFRESH_MS : false,
        });
    // keepPreviousData evita lo sfarfallio ai refresh, ma cambiando evento il dato vecchio non va mostrato
    const alert = lastAlert && summary && lastAlert.imei === summary.imei && lastAlert.id === summary.id ? lastAlert : null;

    if (status === "loading") return (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
    );
    // pedali in ordine di ultima rilevazione, i mai rilevati in fondo
    const devices = [...(board?.devices || [])].sort((x, y) => desc(time(x.timestamp), time(y.timestamp)));
    if (!devices.length) return empty || (
        <Typography color="text.secondary" sx={{ p: 2 }}>{t('noPedalPosition')}</Typography>
    );

    const points = alert?.points || [];
    // le posizioni si riconoscono per indice: piu' pacchetti possono avere la stessa ora di ricezione
    const indexed = points.map((p, i) => ({ p, i }));

    return (
        <Box sx={{
            display: "grid", gridTemplateColumns: { xs: "1fr", md: "320px 1fr" }, height,
            border: `1px solid ${LINE}`, borderRadius: RADIUS, overflow: "hidden", bgcolor: "#fff",
        }}>
            {/* da computer pedali ed eventi hanno meta' colonna ciascuno, ognuno col suo scorrimento: con molti
                pedali gli eventi non finiscono in fondo; da telefono scorre la pagina intera */}
            <Box sx={{
                order: { xs: 2, md: 1 }, minHeight: 0, borderRight: { md: `1px solid ${LINE}` },
                display: { md: "flex" }, flexDirection: "column",
            }}>
                <Box sx={{ flex: { md: "1 1 0" }, minHeight: 0, overflowY: { md: "auto" } }}>
                    <SectionLabel>{t('pedalDevices')}</SectionLabel>
                    {devices.map(d => <DeviceCard key={d.imei} device={d} hidden={hidden.has(d.imei)}
                        onToggle={() => toggleHidden(d.imei)} />)}
                </Box>
                <Box sx={{ flex: { md: "1 1 0" }, minHeight: 0, overflowY: { md: "auto" }, borderTop: { md: `1px solid ${LINE}` } }}>
                    <SectionLabel>{t('pedalAlerts')}</SectionLabel>
                    {!board.alertsAvailable
                        ? <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>{t('pedalAlertsUnavailable')}</Typography>
                        : !alerts.length
                            ? <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>{t('pedalNoAlerts')}</Typography>
                            : <List disablePadding>
                                {alerts.map(a =>
                                    <AlertRow key={`${a.imei}/${a.id}`} a={a} selected={summary === a}
                                        showImei={devices.length > 1}
                                        onSelect={() => setSelected({ imei: a.imei, id: a.id })}>
                                        <AlertPositions points={indexed}
                                            status={alert ? alertStatus : alertStatus === "error" ? "error" : "loading"}
                                            focused={focused} onFocus={setFocused} />
                                    </AlertRow>)}
                            </List>}
                </Box>
            </Box>
            <Box sx={{ order: { xs: 1, md: 2 }, height: MAP_HEIGHT, position: "relative" }}>
                <BoardMap devices={devices.filter(d => !hidden.has(d.imei))} alert={summary ? alert : null}
                    focused={focused} onFocus={setFocused} />
                {summary && alert &&
                    <Box sx={{
                        position: "absolute", right: 12, bottom: 24, bgcolor: "#fff", borderRadius: "8px",
                        boxShadow: "0 2px 10px rgba(0,0,0,.15)", px: 1.5, py: 1, minWidth: 180,
                    }}>
                        <Figure label={t('pedalRoute')} value={`${alert.distanceKm.toFixed(1)} km`} />
                        <Figure label={t('pedalDuration')} value={formatMinutes(alert.durationMinutes)} />
                        <Figure label={t('pedalPoints')} value={alert.points.length} />
                    </Box>}
            </Box>
        </Box>
    );
}

/** Riga di un evento: data, durata e posizioni, icona del tipo; sotto quello selezionato, le sue posizioni. */
function AlertRow({ a, selected, showImei, onSelect, children }) {
    const { t } = useTranslation();
    return (
        <Box sx={{ borderBottom: `1px solid ${LINE}` }}>
            <ListItemButton selected={selected} onClick={onSelect}
                sx={{
                    alignItems: "flex-start", px: 1.5, py: 1.25, borderLeft: "3px solid transparent",
                    "&.Mui-selected, &.Mui-selected:hover": { bgcolor: BLUE_TINT, borderLeftColor: BLUE },
                }}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: selected ? BLUE : "text.primary" }}>
                        {formatDateTime(a.startedAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                        {a.completedAt ? formatMinutes(a.durationMinutes) : t('pedalAlertInProgress')}
                        {a.points != null && ` · ${a.points} ${t('pedalAlertPositions').toLowerCase()}`}
                        {showImei && ` · UGO-${shortImei(a.imei)}`}
                    </Typography>
                </Box>
                <EventIcon />
            </ListItemButton>
            {selected && children}
        </Box>
    );
}

/** Icona del tipo di evento: oggi il server UGO espone solo gli allarmi furto (lucchetto rosso). */
function EventIcon() {
    return (
        <Box sx={{
            width: 22, height: 22, borderRadius: "6px", bgcolor: RED_TINT, color: RED, display: "flex",
            alignItems: "center", justifyContent: "center", flexShrink: 0, ml: 1, mt: 0.25,
        }}>
            <LockOutlinedIcon sx={{ fontSize: 15 }} />
        </Box>
    );
}

/**
 * Ultimi dati noti del pedale: stato, ultima rilevazione, coordinate e batteria, in riquadri come la Web Board.
 * Nascosto dalla mappa, si riduce al solo nome con l'interruttore per mostrarlo di nuovo.
 */
function DeviceCard({ device, hidden, onToggle }) {
    const { t } = useTranslation();
    const toggle = (
        <Tooltip title={t(hidden ? 'pedalShowOnMap' : 'pedalHideOnMap')}>
            <IconButton size="small" onClick={onToggle} aria-label={t(hidden ? 'pedalShowOnMap' : 'pedalHideOnMap')}
                sx={{ ml: "auto !important", color: "text.secondary" }}>
                {hidden ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
            </IconButton>
        </Tooltip>
    );
    if (hidden) return (
        <Box sx={{ px: 1.5, py: 1, borderBottom: `1px solid ${LINE}` }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontWeight: 800, color: "text.secondary" }}>UGO-{shortImei(device.imei)}</Typography>
                {toggle}
            </Stack>
        </Box>
    );
    const mode = MODES[String(device.mode)];
    const hasPosition = device.latitude != null && device.longitude != null;
    const battery = device.batteryPercentage;
    const batteryColor = battery == null ? GREEN : battery < 20 ? RED : battery < 40 ? AMBER : GREEN;
    // pedale mai rilevato: una riga sola invece di tre riquadri vuoti
    const noData = !device.timestamp && !hasPosition && battery == null;
    return (
        <Box sx={{ p: 1.5, borderBottom: `1px solid ${LINE}` }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: noData ? 0.25 : 1.25 }}>
                <Typography sx={{ fontWeight: 800 }}>UGO-{shortImei(device.imei)}</Typography>
                {mode &&
                    <Stack direction="row" alignItems="center" spacing={0.5}
                        sx={{ bgcolor: mode.bg, color: mode.color, borderRadius: "999px", px: 1, py: 0.25 }}>
                        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: mode.color }} />
                        <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{t(mode.label)}</Typography>
                    </Stack>}
                {toggle}
            </Stack>
            {noData ? <Typography variant="caption" color="text.secondary">{t('noPedalPosition')}</Typography> :
            <Stack spacing={0.75}>
                <InfoBox label={t('pedalLastDetection')}>
                    {device.timestamp ? formatDetection(device.timestamp) : "-"}
                </InfoBox>
                <InfoBox label={t('coordinates')}>
                    <Box component="span" sx={{ fontFamily: MONO }}>
                        {hasPosition ? formatCoords(device.latitude, device.longitude, 4) : "-"}
                    </Box>
                </InfoBox>
                <InfoBox label={t('pedalBattery')}>
                    {battery == null ? "-" :
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, battery))}
                                sx={{
                                    flex: 1, height: 5, borderRadius: 3, bgcolor: "#e2e4e8",
                                    "& .MuiLinearProgress-bar": { bgcolor: batteryColor, borderRadius: 3 },
                                }} />
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: batteryColor }}>{battery}%</Typography>
                        </Stack>}
                </InfoBox>
            </Stack>}
        </Box>
    );
}

/**
 * Titolo di una meta' della colonna di sinistra; da computer resta in cima mentre la sua lista scorre (da
 * telefono scorre la pagina, e si incollerebbe sotto l'header fisso).
 */
function SectionLabel({ children }) {
    return (
        <Typography sx={{
            position: { md: "sticky" }, top: 0, zIndex: 1, bgcolor: BOX_BG, borderBottom: `1px solid ${LINE}`,
            borderLeft: `3px solid ${BLUE}`, px: 1.5, py: 1, fontSize: 13, fontWeight: 800, letterSpacing: ".06em",
            textTransform: "uppercase", color: "#1f2937",
        }}>
            {children}
        </Typography>
    );
}

function InfoBox({ label, children }) {
    return (
        <Box sx={{ bgcolor: BOX_BG, borderRadius: "8px", px: 1.25, py: 0.75 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#8a8f98" }}>
                {label}
            </Typography>
            <Typography component="div" sx={{ fontSize: 13, fontWeight: 700, mt: 0.25 }}>{children}</Typography>
        </Box>
    );
}

function AlertPositions({ points, status, focused, onFocus }) {
    const { t } = useTranslation();
    if (status === "loading") return <LinearProgress sx={{ mx: 1.5, mb: 1 }} />;
    if (status === "error") return (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", px: 1.5, pb: 1 }}>
            {t('pedalAlertsUnavailable')}
        </Typography>
    );
    return (
        <Box sx={{ mx: 1.5, mb: 1, pl: 1.25, borderLeft: `2px solid ${BLUE}` }}>
            <Typography variant="caption" color="text.secondary">
                {t('pedalAlertPositions')} · {t('alertTimeIsReceive')}
            </Typography>
            <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
                {points.map(({ p, i }) => (
                    <Box key={i} onClick={() => onFocus(i)}
                        sx={{
                            display: "flex", gap: 1.5, py: 0.5, px: 0.75, cursor: "pointer", borderRadius: "6px",
                            bgcolor: focused === i ? BLUE_TINT : "transparent", "&:hover": { bgcolor: BLUE_TINT },
                        }}>
                        <Typography variant="caption" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
                            {formatTime(p.time)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>
                            {formatCoords(p.latitude, p.longitude, 5)}
                        </Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

/**
 * Indicazioni stradali verso un punto. Il link universale di Google Maps apre l'app se e' installata (Android,
 * iOS con Google Maps), altrimenti Google Maps nel browser: va bene da computer e da telefono.
 */
const directionsUrl = (lat, lng) => `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;

/** Contenuto del fumetto di un punto: cosa e' (facoltativo), ora e link per raggiungerlo. */
function PointInfo({ title, time, lat, lng }) {
    const { t } = useTranslation();
    return (
        <Box sx={{ pr: 0.5 }}>
            {title && <Typography variant="caption" color="text.secondary" component="div">{title}</Typography>}
            {time && <Typography variant="body2" fontWeight={600}>{dayjs(new Date(time)).format("DD/MM/YYYY HH:mm:ss")}</Typography>}
            <Link href={directionsUrl(lat, lng)} target="_blank" rel="noopener noreferrer" underline="hover"
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mt: 0.5, fontSize: 13, fontWeight: 600, color: BLUE }}>
                <DirectionsIcon sx={{ fontSize: 16 }} />{t('pedalDirections')}
            </Link>
        </Box>
    );
}

/**
 * Mappa del pannello: l'ultima posizione rilevata di ogni pedale e' sempre visibile (segnaposto rosso), e con un
 * evento selezionato ci sono anche il suo percorso e i suoi punti. Toccando un punto o un segnaposto si apre un
 * fumetto con l'ora e il link per raggiungerlo.
 */
function BoardMap({ devices, alert, focused, onFocus }) {
    const { t } = useTranslation();
    const [focusedDevice, setFocusedDevice] = useState(null);
    const points = useMemo(() => alert?.points || [], [alert]);
    const focusedPoint = focused != null ? points[focused] : null;
    const path = useMemo(() => points.map(p => ({ lat: p.latitude, lng: p.longitude })), [points]);
    const located = devices.filter(d => d.latitude != null && d.longitude != null);
    const devicePositions = located.map(d => ({ lat: d.latitude, lng: d.longitude }));
    const first = located[0];
    const shownDevice = located.find(d => d.imei === focusedDevice);
    // l'inquadratura si rifa' solo quando cambia cosa si guarda (pedali mostrati, evento scelto, percorso
    // arrivato), mai per i nuovi dati del refresh: chi ha zoomato o spostato la mappa resta dov'e'
    // (IMEI ordinati: i pedali sono in ordine di ultima rilevazione, che a un refresh puo' cambiare)
    const frameKey = `${located.map(d => d.imei).sort().join(",")}|${alert ? `${alert.imei}/${alert.id}` : ""}|${path.length > 0}`;
    // centro legato all'inquadratura: MapContainer ricentra la mappa a ogni cambio di riferimento
    const center = useMemo(() => first ? { lat: first.latitude, lng: first.longitude } : path[path.length - 1],
        [frameKey]); // eslint-disable-line react-hooks/exhaustive-deps
    // il pallino da seguire: l'ultima posizione dell'evento in corso, altrimenti il pedale rilevato per ultimo
    const target = alert && !alert.completedAt && path.length ? path[path.length - 1]
        : first ? { lat: first.latitude, lng: first.longitude } : null;
    const [following, setFollowing] = useState(false);
    useEffect(() => setFollowing(false), [frameKey]);

    // un fumetto alla volta
    const focusPoint = i => { setFocusedDevice(null); onFocus(i); };
    const focusDevice = imei => { onFocus(null); setFocusedDevice(imei); };

    return (<>
        <MapContainer center={center} zoom={DEVICE_ZOOM}>
            {path.length > 0 && <>
                <Polyline path={path} options={{ strokeColor: BLUE, strokeOpacity: 0.9, strokeWeight: 4 }} />
                {points.map((p, i) => (
                    <AdvancedMarker key={i} position={path[i]} onClick={() => focusPoint(i)}
                        icon={focused === i ? DOT_FOCUS : i === 0 || i === points.length - 1 ? DOT_END : DOT}
                        zIndex={focused === i ? 3 : i === points.length - 1 ? 2 : 1} />
                ))}
                {focusedPoint &&
                    <InfoWindow position={{ lat: focusedPoint.latitude, lng: focusedPoint.longitude }}
                        onCloseClick={() => onFocus(null)}>
                        <PointInfo time={focusedPoint.time} lat={focusedPoint.latitude} lng={focusedPoint.longitude} />
                    </InfoWindow>}
            </>}
            {located.map(d => (
                <AdvancedMarker key={d.imei} position={{ lat: d.latitude, lng: d.longitude }} zIndex={4}
                    onClick={() => focusDevice(d.imei)} />
            ))}
            {shownDevice &&
                <InfoWindow position={{ lat: shownDevice.latitude, lng: shownDevice.longitude }}
                    onCloseClick={() => setFocusedDevice(null)}>
                    <PointInfo title={`UGO-${shortImei(shownDevice.imei)} · ${t('pedalLastDetection')}`}
                        time={shownDevice.timestamp} lat={shownDevice.latitude} lng={shownDevice.longitude} />
                </InfoWindow>}
            <FitBounds path={path} extra={devicePositions} focused={focused} frameKey={frameKey} />
            <Follow target={following ? target : null} onStop={() => setFollowing(false)} />
        </MapContainer>
        {target &&
            <Tooltip title={t('pedalRecenter')} placement="left">
                <IconButton onClick={() => setFollowing(true)} aria-label={t('pedalRecenter')}
                    sx={{
                        position: "absolute", top: 10, right: 10, width: 40, height: 40, borderRadius: "2px",
                        bgcolor: "#fff", boxShadow: "0 1px 4px rgba(0,0,0,.3)", "&:hover": { bgcolor: "#f5f5f5" },
                        color: following ? BLUE : "#666",
                    }}>
                    {following ? <MyLocationIcon /> : <LocationSearchingIcon />}
                </IconButton>
            </Tooltip>}
    </>);
}

/**
 * Segue il pallino senza toccare lo zoom: a ogni nuova posizione la mappa ci si sposta sopra. Smette quando
 * chi guarda trascina la mappa, che da li' in poi resta dove l'ha messa.
 */
function Follow({ target, onStop }) {
    const map = useGoogleMap();
    useEffect(() => {
        if (map && target) map.panTo(target);
    }, [map, target?.lat, target?.lng]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!map || !target || !window.google?.maps) return;
        const listener = map.addListener("dragstart", onStop);
        return () => window.google.maps.event.removeListener(listener);
    }, [map, !!target]); // eslint-disable-line react-hooks/exhaustive-deps
    return null;
}

/**
 * Adatta la mappa al percorso dell'evento e all'ultima posizione dei pedali, cosi' entrambi restano in vista;
 * con un punto selezionato, centra su quello. Si rifa' solo quando cambia `frameKey`.
 */
function FitBounds({ path, extra, focused, frameKey }) {
    const map = useGoogleMap();
    const all = [...path, ...extra];
    useEffect(() => {
        if (!map || !all.length || !window.google?.maps) return;
        // dopo il giro in cui MapContainer applica il proprio centro, altrimenti lo sposterebbe di nuovo
        const timer = setTimeout(() => {
            if (all.length === 1) {
                map.setCenter(all[0]);
                map.setZoom(DEVICE_ZOOM);
                return;
            }
            const bounds = new window.google.maps.LatLngBounds();
            all.forEach(p => bounds.extend(p));
            map.fitBounds(bounds, 48);
            // un evento breve sta in pochi metri: senza tetto lo zoom andrebbe ben oltre quello del pedale
            window.google.maps.event.addListenerOnce(map, "idle", () => {
                if (map.getZoom() > DEVICE_ZOOM + 2) map.setZoom(DEVICE_ZOOM + 2);
            });
        }, 0);
        return () => clearTimeout(timer);
    }, [map, frameKey]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (map && focused != null && path[focused]) map.panTo(path[focused]);
    }, [map, focused]); // eslint-disable-line react-hooks/exhaustive-deps
    return null;
}

/** Riga del riepilogo sulla mappa: etichetta a sinistra, valore a destra. */
function Figure({ label, value }) {
    return (
        <Stack direction="row" justifyContent="space-between" spacing={3} sx={{ py: 0.25 }}>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{label}</Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{value}</Typography>
        </Stack>
    );
}
